import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Typography, Grid } from "@mui/material";

import MonthlyKPIsScreen from "../pages/MonthlyKPIsScreen";
import MembershipReportScreen from "../pages/MembershipReportScreen";

import FiltersReport from "../components/FiltersReport";
import { buildFiltersReportExcelSection } from "../components/FiltersReport";
import ReportBuilder, {
    BREAK_LINE,
    CUSTOM,
    fields_OverviewTable,
    fields_OverviewTable_Vouchers,
    TAB,
    TABS,
} from "../components/shared/ReportBuilder/ReportBuilder";

import { HOTELS_INFO } from "../configuration";
import {
    DATATYPE_CHECKIN_NAME,
    DATATYPE_RESDATE,
    DATATYPE_RESDATE_NAME,
    DEFAULT_COMPARISON,
    DISPLAY_ELEMENT_HELPER,
    GROUP_COUNTRIES,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    GROUP_EXTRAS,
    GROUP_OFFERS,
    GROUP_ROOM,
    GROUP_VOUCHERS,
    PRESET_ANYTIME,
    PRESET_LAST_YEAR,
    PRESET_MONTH_TO_DATE,
    PresetDates,
} from "../helpers/filters";
import { setHotelID } from "../actions/hotelID";
import { setTemplateKey } from "../actions/PDFExport";
import ExtrasScreen from "../pages/ExtrasScreen";
import { TARGET_OFF } from "../helpers/targets";
import { REPORT_CHART_BARS_CATEGORY } from "../components/shared/ReportBuilder/ReportChartBarsCategory";
import { REPORT_CHART_TREND_DAILY_WEEKLY_MONTHLY } from "../components/shared/ReportBuilder/ReportChartTrendDailyWeeklyMonthly";
import { REPORT_CHART_WORLD_MAP } from "../components/shared/ReportBuilder/ReportChartWorldMap";
import { REPORT_TABLE_METRICS_SUMMARY } from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import { REPORT_TABLE_OVERVIEW } from "../components/shared/ReportBuilder/ReportTableOverview";
import { fullExport } from "../helpers/export";
import { buildExcelSectionsFromReports } from "../helpers/excelSections";

export const REPORTING_REPORT_KEY = "REPORTING";

export const getAroReports = ({ hotelID, js, PDF = false }) => {
    const dateTypeDescription =
        js?.dataType === DATATYPE_RESDATE
            ? DATATYPE_RESDATE_NAME
            : DATATYPE_CHECKIN_NAME;
    const fields =
        (js?.filters?.Extras ?? []).length > 0
            ? [
                  "revenueTotal",
                  "revenueExtras",
                  "numberOfExtras",
                  "bookingsWithExtras",
                  "roomNightsTotal",
              ]
            : [
                  "revenueTotal",
                  "revenueRoom",
                  "revenueRoomAndExtras",
                  "revenueExtras",
                  "bookingsRooms",
                  "roomNightsTotal",
                  "bookingsCancelled",
                  "cancellationRate",
                  "ABWTotal",
                  "ALoSTotal",
                  "ADRTotal",
                  "empty",
                  "revenueVouchers",
                  "vouchersBookings",
                  "vouchersCount",
              ];

    return [
        ...(PDF
            ? [
                  {
                      type: CUSTOM,
                      groupBy: [],
                      title: "Title",
                      skipExcelDataProcessing: true,
                      exportExcel: () => ({
                          id: "report-title",
                          kind: "title",
                          value: "Reporting",
                      }),
                      options: {
                          duplicable: false,
                          disableInteraction: true,
                      },
                      render: () => (
                          <Typography variant="h4">Reporting</Typography>
                      ),
                  },
                  {
                      type: CUSTOM,
                      groupBy: [],
                      title: "Filters",
                      skipExcelDataProcessing: true,
                      exportExcel: buildFiltersReportExcelSection,
                      options: {
                          duplicable: false,
                      },
                      render: () => (
                          <FiltersReport
                              hideExports
                              hide={[
                                  "ARRIVAL_PERIOD",
                                  "TIME_ARRIVAL_RANGE",
                                  "RESDATE_PERIOD",
                                  "TIME_BOOKING_RANGE",
                                  "FILTERS_TYPES",
                              ]}
                              defaultValues={{
                                  presetDate: PRESET_MONTH_TO_DATE,
                                  presetArrivalDate: PRESET_ANYTIME,
                                  presetResDate: PRESET_ANYTIME,
                                  dataType: DATATYPE_RESDATE,
                                  filters: {
                                      Cancellations: "included",
                                      Comparison: DEFAULT_COMPARISON,
                                      ComparisonCustom: {
                                          ...PresetDates(PRESET_LAST_YEAR),
                                      },
                                      Target: TARGET_OFF,
                                  },
                              }}
                          />
                      ),
                  },
              ]
            : []),
        {
            type: REPORT_TABLE_METRICS_SUMMARY,
            groupBy: [],
            fieldsOrder: fields,
            hideFields: [
                ...Object.keys(DISPLAY_ELEMENT_HELPER).filter(
                    (e) => !fields.includes(e),
                ),
            ],
            props: { hideEvents: false },
        },
        ...(!PDF ? [{ type: BREAK_LINE, quantity: 1 }] : []),
        {
            type: TABS,
            default:
                HOTELS_INFO[hotelID]?.type === "VouchersOnly"
                    ? "Vouchers"
                    : "Rate Plan",
            tabs: [
                {
                    type: TAB,
                    title: "Rate Plan",
                    reports: [
                        {
                            type: REPORT_CHART_BARS_CATEGORY,
                            title: "Bar chart",
                            description: `Ranks rate plans by the selected metric for the selected period. Dates are based on ${dateTypeDescription}. The top five rate plans are displayed separately and the remaining ones are grouped as Others.`,
                            groupBy: [GROUP_OFFERS],
                            metric: "bookingsTotal",
                            metrics: {
                                ...DISPLAY_ELEMENT_HELPER,
                                bookingsTotal: {
                                    ...DISPLAY_ELEMENT_HELPER.bookingsTotal,
                                    short: "Bookings total",
                                },
                            },
                            props: {
                                title: "Metrics by rate plan",
                                description: `Ranks rate plans by the selected metric for the selected period. Dates are based on ${dateTypeDescription}. The top five rate plans are displayed separately and the remaining ones are grouped as Others.`,
                            },
                        },
                        {
                            type: REPORT_CHART_TREND_DAILY_WEEKLY_MONTHLY,
                            title: "Trend chart",
                            groupBy: [
                                GROUP_OFFERS,
                                GROUP_DATE_YEAR,
                                GROUP_DATE_MONTH,
                                GROUP_DATE_DAY,
                            ],
                            filters: { Types: ["Special Offer"] },
                            props: {
                                title: "Trend by rate plan",
                                description: `Shows the daily, weekly or monthly trend for the leading rate plans. Dates are based on ${dateTypeDescription}.`,
                                showLastYear: false,
                                defaultBreakdown: "weekly",
                            },
                        },
                        {
                            type: REPORT_TABLE_OVERVIEW,
                            title: "Overview table",
                            groupBy: [GROUP_OFFERS],
                            ...fields_OverviewTable,
                            props: {
                                title: "Overview by rate plan",
                                description:
                                    "Shows the selected metrics by rate plan, including the current period, comparison period and percentage change.",
                            },
                        },
                    ],
                },
                {
                    type: TAB,
                    title: "Room Type",
                    reports: [
                        {
                            type: REPORT_CHART_BARS_CATEGORY,
                            title: "Bar chart",
                            groupBy: [GROUP_ROOM],
                            metric: "bookingsTotal",
                            metrics: {
                                ...DISPLAY_ELEMENT_HELPER,
                                bookingsTotal: {
                                    ...DISPLAY_ELEMENT_HELPER.bookingsTotal,
                                    short: "Bookings total",
                                },
                            },
                            props: {
                                title: "Metrics by room type",
                                description: `Ranks room types by the selected metric for the selected period. Dates are based on ${dateTypeDescription}. The top five room types are displayed separately and the remaining ones are grouped as Others.`,
                            },
                        },
                        {
                            type: REPORT_CHART_TREND_DAILY_WEEKLY_MONTHLY,
                            title: "Trend chart",
                            groupBy: [
                                GROUP_ROOM,
                                GROUP_DATE_YEAR,
                                GROUP_DATE_MONTH,
                                GROUP_DATE_DAY,
                            ],
                            filters: { Types: ["Special Offer"] },
                            props: {
                                title: "Trend by room type",
                                description: `Shows the daily, weekly or monthly trend for the leading room types. Dates are based on ${dateTypeDescription}.`,
                                showLastYear: false,
                                defaultBreakdown: "weekly",
                            },
                        },
                        {
                            type: REPORT_TABLE_OVERVIEW,
                            title: "Overview table",
                            groupBy: [GROUP_ROOM],
                            ...fields_OverviewTable,
                            sortable: [],
                            props: {
                                title: "Overview by room type",
                                description:
                                    "Shows the selected metrics by room type, including the current period, comparison period and percentage change.",
                            },
                        },
                    ],
                },
                {
                    type: TAB,
                    title: "Geographical Market",
                    reports: [
                        {
                            type: REPORT_CHART_WORLD_MAP,
                            title: "Map",
                            groupBy: [GROUP_COUNTRIES],
                            props: {
                                title: "Bookings by country",
                                description: `Shows bookings after cancellations by guest country. Dates are based on ${dateTypeDescription}. Colour intensity represents booking volume.`,
                            },
                        },
                        {
                            type: REPORT_CHART_TREND_DAILY_WEEKLY_MONTHLY,
                            title: "Trend chart",
                            groupBy: [
                                GROUP_COUNTRIES,
                                GROUP_DATE_YEAR,
                                GROUP_DATE_MONTH,
                                GROUP_DATE_DAY,
                            ],
                            props: {
                                title: "Trend by country",
                                description: `Shows the daily, weekly or monthly trend for the leading guest countries. Dates are based on ${dateTypeDescription}.`,
                                showLastYear: false,
                                defaultBreakdown: "weekly",
                            },
                        },
                        {
                            type: REPORT_TABLE_OVERVIEW,
                            title: "Overview table",
                            groupBy: [GROUP_COUNTRIES],
                            ...fields_OverviewTable,
                            props: {
                                title: "Overview by country",
                                description:
                                    "Shows the selected metrics by guest country, including the current period, comparison period and percentage change.",
                            },
                        },
                    ],
                },
                {
                    type: TAB,
                    title: "Extras",
                    reports: [
                        {
                            type: CUSTOM,
                            groupBy: [],
                            title: "Bar chart",
                            options: {
                                duplicable: true,
                            },
                            render: ({ js, hotelID, data }) => (
                                <ExtrasScreen
                                    js={js}
                                    hotelID={hotelID}
                                    data={data}
                                    title="Metrics by extras"
                                    description={`Ranks extras by the selected metric for the selected period. Dates are based on ${dateTypeDescription}. The top five extras are displayed separately and the remaining ones are grouped as Others.`}
                                />
                            ),
                        },
                        {
                            type: REPORT_CHART_TREND_DAILY_WEEKLY_MONTHLY,
                            title: "Trend chart",
                            groupBy: [
                                GROUP_EXTRAS,
                                GROUP_DATE_YEAR,
                                GROUP_DATE_MONTH,
                                GROUP_DATE_DAY,
                            ],
                            props: {
                                title: "Trend by extras",
                                description: `Shows the daily, weekly or monthly revenue and number sold for the leading extras. Dates are based on ${dateTypeDescription}.`,
                                showLastYear: false,
                                defaultBreakdown: "weekly",
                                metrics: {
                                    revenueSelectedExtra: {
                                        ...DISPLAY_ELEMENT_HELPER.revenueSelectedExtra,
                                        short: "Revenue",
                                    },
                                    countSelectedExtra: {
                                        ...DISPLAY_ELEMENT_HELPER.countSelectedExtra,
                                        short: "Number sold",
                                    },
                                },
                            },
                        },
                        {
                            type: REPORT_TABLE_OVERVIEW,
                            title: "Overview table",
                            groupBy: [GROUP_EXTRAS],
                            fieldsOrder: [
                                "countSelectedExtra",
                                "revenueSelectedExtra",
                                "bookingsRooms",
                                "roomNightsTotal",
                            ],
                            hideFields: [
                                ...Object.keys(DISPLAY_ELEMENT_HELPER).filter(
                                    (e) =>
                                        ![
                                            "countSelectedExtra",
                                            "revenueSelectedExtra",
                                            "bookingsRooms",
                                            "roomNightsTotal",
                                        ].includes(e),
                                ),
                            ],
                            props: {
                                title: "Overview by extras",
                                description:
                                    "Shows revenue, units sold, bookings and room nights for each extra.",
                                place: "ReportScreen",
                            },
                        },
                    ],
                },
                {
                    type: TAB,
                    isVoucher: true,
                    title: "Vouchers",
                    reports: [
                        {
                            type: REPORT_CHART_BARS_CATEGORY,
                            title: "Bar chart",
                            groupBy: [GROUP_VOUCHERS],
                            metric: "vouchersCount",
                            metrics: Object.entries(
                                DISPLAY_ELEMENT_HELPER,
                            ).reduce((acc, [key, value]) => {
                                acc[key] =
                                    key === "revenueVouchers" ||
                                    key === "vouchersCount"
                                        ? {
                                              ...value,
                                              important: true,
                                              short:
                                                  "revenueVouchers" === key
                                                      ? "Revenue"
                                                      : "Number sold",
                                          }
                                        : { ...value, important: false };
                                return acc;
                            }, {}),
                            filters: { Types: ["Voucher"] },
                            props: {
                                title: "Metrics by voucher",
                                description: `Ranks vouchers by the selected metric for the selected period. Dates are based on ${dateTypeDescription}. The top five vouchers are displayed separately and the remaining ones are grouped as Others.`,
                                showVouchers: true,
                            },
                        },
                        {
                            type: REPORT_CHART_TREND_DAILY_WEEKLY_MONTHLY,
                            title: "Trend chart",
                            groupBy: [
                                GROUP_VOUCHERS,
                                GROUP_DATE_YEAR,
                                GROUP_DATE_MONTH,
                                GROUP_DATE_DAY,
                            ],
                            filters: { Types: ["Voucher"] },
                            props: {
                                title: "Trend by voucher",
                                description: `Shows the daily, weekly or monthly revenue and number sold for the leading vouchers. Dates are based on ${dateTypeDescription}.`,
                                showVouchers: true,
                                showLastYear: false,
                                defaultBreakdown: "weekly",
                                metrics: {
                                    revenueVouchers: {
                                        ...DISPLAY_ELEMENT_HELPER.revenueVouchers,
                                        short: "Revenue",
                                    },
                                    vouchersCount: {
                                        ...DISPLAY_ELEMENT_HELPER.vouchersCount,
                                        short: "Number sold",
                                    },
                                },
                            },
                        },
                        {
                            type: REPORT_TABLE_OVERVIEW,
                            title: "Overview table",
                            groupBy: [GROUP_VOUCHERS],
                            filters: { Types: ["Voucher"] },
                            props: {
                                title: "Overview by voucher",
                                description:
                                    "Shows voucher revenue, bookings, units sold and value ranges for each voucher.",
                                showVouchers: true,
                            },
                            ...fields_OverviewTable_Vouchers,
                        },
                    ],
                },
            ],
        },
    ];
};

const Dashboard = ({ handleProcessedData, processedData }) => {
    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const search = useLocation().search;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const URLpresetDate = new URLSearchParams(search).get("presetDate");
    const URLhotelID = new URLSearchParams(search).get("hotelid");

    const reports = getAroReports({ hotelID, js });

    useEffect(() => {
        if (URLhotelID) dispatch(setHotelID(URLhotelID));
    }, [URLhotelID]);

    const onExcelExport = ({
        js: activeJs,
        processedData,
        hotelID,
        activeFiltersRows,
        dataBookings,
        dataSimple,
        dataTargets,
    }) => {
        const { sections } = buildExcelSectionsFromReports({
            reports: getAroReports({ hotelID, js: activeJs, PDF: true }),
            js: activeJs,
            hotelID,
            dataBookings,
            dataSimple,
            dataTargets,
            extraContext: { activeFiltersRows },
        });

        const titleSection = sections.find(
            (section) => section.kind === "title",
        );
        const filtersSection = sections.find(
            (section) =>
                section.kind === "keyValue" && section.id === "active-filters",
        );

        fullExport(activeJs, processedData, hotelID, {
            title: titleSection?.value ?? "Reporting",
            fileName: "Reporting - AroAnalytics.xlsx",
            includeActiveFilters: true,
            activeFiltersRows: filtersSection?.rows ?? activeFiltersRows,
        });
    };

    return (
        <>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="h4">Reporting</Typography>
                </Grid>
            </Grid>{" "}
            <FiltersReport
                onExcelExport={onExcelExport}
                onPDFExport={() => {
                    dispatch(setTemplateKey(REPORTING_REPORT_KEY));
                    navigate("/pdfexport");
                }}
                hide={[
                    "ARRIVAL_PERIOD",
                    "TIME_ARRIVAL_RANGE",
                    "RESDATE_PERIOD",
                    "TIME_BOOKING_RANGE",
                    "FILTERS_TYPES",
                ]}
                defaultValues={{
                    presetDate: URLpresetDate ?? PRESET_MONTH_TO_DATE,
                    presetArrivalDate: PRESET_ANYTIME,
                    presetResDate: PRESET_ANYTIME,
                    dataType: DATATYPE_RESDATE,
                    filters: {
                        Cancellations: "included",
                        Comparison: DEFAULT_COMPARISON,
                        ComparisonCustom: {
                            ...PresetDates(PRESET_LAST_YEAR),
                        },
                        Target: TARGET_OFF,
                    },
                }}
                setProcessed={handleProcessedData}
            />
            <ReportBuilder reports={reports} />
        </>
    );
};

const AroReportScreen = ({}) => {
    const [processedData, setProcessedData] = useState({});

    const handleProcessedData = (data) => {
        setProcessedData(data);
    };

    return (
        <>
            <Routes>
                <Route
                    path=""
                    element={
                        <Dashboard
                            handleProcessedData={handleProcessedData}
                            processedData={processedData}
                        />
                    }
                />
                <Route
                    path="dashboard"
                    element={
                        <Dashboard
                            handleProcessedData={handleProcessedData}
                            processedData={processedData}
                        />
                    }
                />
                <Route path="kpis" element={<MonthlyKPIsScreen isolated />} />
                <Route
                    path="membership"
                    element={
                        <>
                            <br />
                            <MembershipReportScreen />
                        </>
                    }
                />
            </Routes>
        </>
    );
};
export default AroReportScreen;
